import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Send, TicketIcon, Clock, User, Lock, MessageSquare, Activity, Paperclip, CheckCircle2,
} from "lucide-react";
import {
  useGetMyTicketQuery,
  useAddReplyMutation,
  useResolveMyTicketMutation,
  useCloseMyTicketMutation,
} from "../api/supportApi";
import { StatusBadge, PriorityBadge } from "../components/TicketBadges";
import AttachmentChips from "../components/AttachmentChips";
import FileUploader from "../components/FileUploader";

function fmt(d: string) {
  return new Date(d).toLocaleString("en-NG", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const PRIORITY_RING: Record<string, string> = {
  urgent: "ring-2 ring-red-200",
  high:   "ring-2 ring-orange-200",
  medium: "",
  low:    "",
};

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading } = useGetMyTicketQuery(id!);
  const [addReply, { isLoading: replying, error: replyError }] = useAddReplyMutation();
  const [resolveTicket, { isLoading: resolving, error: resolveError }] = useResolveMyTicketMutation();
  const [closeTicket] = useCloseMyTicketMutation();

  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const MAX_CHARS = 2000;

  // Auto-close: if last staff reply is 3+ days old and member has not replied since
  useEffect(() => {
    if (!ticket || !id) return;
    if (ticket.status === "closed" || ticket.status === "resolved") return;

    const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
    const visible = (ticket.replies ?? []).filter((r) => !r.is_internal);
    if (visible.length === 0) return;

    const last = visible[visible.length - 1];
    const isStaffLast = last.user?.whois === "admin" || last.user?.whois === "executive";
    const elapsed = Date.now() - new Date(last.created_at).getTime();

    if (isStaffLast && elapsed > THREE_DAYS_MS) {
      closeTicket(id);
    }
  }, [ticket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || !id) return;
    await addReply({ id, body: { body: body.trim(), attachments: files.length ? files : undefined } });
    setBody("");
    setFiles([]);
  }

  async function handleResolve() {
    if (!id) return;
    await resolveTicket(id);
  }

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-3xl mx-auto p-4 md:p-6 pb-20 animate-pulse">
        <div className="h-10 bg-gray-200 rounded-xl w-28" />
        <div className="h-32 bg-white rounded-3xl border border-gray-100" />
        <div className="h-56 bg-white rounded-3xl border border-gray-100" />
        <div className="h-32 bg-white rounded-3xl border border-gray-100" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-red-50 border border-red-100 rounded-3xl p-8 text-center">
          <p className="text-red-600 font-black">Ticket not found</p>
        </div>
      </div>
    );
  }

  const isClosed = ticket.status === "closed" || ticket.status === "resolved";

  // Filter internal notes — members must never see them even if the backend leaks them
  const visibleReplies = (ticket.replies ?? []).filter((r) => !r.is_internal);

  const allMessages = [
    {
      id: "original",
      isOriginal: true,
      user: ticket.user,
      body: ticket.description,
      created_at: ticket.created_at,
      isStaff: false,
      attachments: ticket.attachments ?? [],
    },
    ...visibleReplies.map((r) => ({
      id: r.id,
      isOriginal: false,
      user: r.user,
      body: r.body,
      created_at: r.created_at,
      isStaff: r.user?.whois === "admin" || r.user?.whois === "executive",
      attachments: r.attachments ?? [],
    })),
  ];

  return (
    <div className="space-y-5 max-w-3xl mx-auto p-4 md:p-6 pb-20">

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm active:scale-95"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Tickets
      </button>

      {/* Ticket Header */}
      <div className={`bg-white border border-gray-100 rounded-3xl p-6 shadow-sm ${PRIORITY_RING[ticket.priority] ?? ""}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-lg shadow-gray-900/20 shrink-0">
            <TicketIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{ticket.reference}</span>
              {ticket.category && (
                <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md font-semibold">
                  {ticket.category.name}
                </span>
              )}
            </div>
            <h1 className="text-lg font-black text-gray-900 leading-snug mb-3">{ticket.subject}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <span className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold">
                <Clock className="w-3 h-3" />
                {fmt(ticket.created_at)}
              </span>
              {ticket.assignee && (
                <span className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold">
                  <User className="w-3 h-3" />
                  {ticket.assignee.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conversation Thread */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50 flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Conversation · {allMessages.length} message{allMessages.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="px-6 py-2">
          {allMessages.map((msg, idx) => {
            const isLast    = idx === allMessages.length - 1;
            const initial   = msg.user?.name?.[0]?.toUpperCase() ?? (msg.isStaff ? "S" : "U");
            return (
              <div key={String(msg.id)} className="flex gap-4 py-5">
                {/* Avatar + timeline */}
                <div className="flex flex-col items-center shrink-0" style={{ width: 36 }}>
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0 ${
                      msg.isOriginal ? "bg-gray-900" : msg.isStaff ? "bg-indigo-600" : "bg-gray-500"
                    }`}
                  >
                    {initial}
                  </div>
                  {!isLast && <div className="flex-1 w-px bg-gray-100 mt-2" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-sm font-black text-gray-800">
                      {msg.user?.name ?? (msg.isStaff ? "Support Team" : "You")}
                    </span>
                    {msg.isOriginal && (
                      <span className="text-[9px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md font-black uppercase tracking-wider">
                        Original
                      </span>
                    )}
                    {!msg.isOriginal && msg.isStaff && (
                      <span className="text-[9px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md font-black uppercase tracking-wider">
                        Support
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400 ml-auto font-medium" title={fmt(msg.created_at)}>
                      {timeAgo(msg.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                  <AttachmentChips attachments={msg.attachments} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reply Box or Closed Notice */}
      {!isClosed ? (
        <div className="space-y-3">
          <form onSubmit={handleReply} className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reply</span>
            </div>
            <div className="p-5 space-y-4">
              {replyError && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700 font-semibold">
                  {(replyError as any)?.data?.message ?? "Failed to send reply. Please try again."}
                </div>
              )}

              <div className="relative">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, MAX_CHARS))}
                  rows={4}
                  required
                  placeholder="Write your reply…"
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none transition placeholder-gray-400"
                />
                <span className={`absolute bottom-3 right-4 text-[10px] font-bold ${body.length > MAX_CHARS * 0.9 ? "text-red-400" : "text-gray-300"}`}>
                  {body.length}/{MAX_CHARS}
                </span>
              </div>

              {/* Attachment uploader */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Paperclip className="w-3 h-3" />
                  Attachments <span className="font-normal normal-case tracking-normal">(optional)</span>
                </p>
                <FileUploader files={files} onChange={setFiles} maxFiles={5} disabled={replying} />
              </div>

              <div className="flex items-center justify-between">
                <p className="text-[10px] text-gray-400 font-medium hidden sm:block">
                  Be specific — it helps us resolve your issue faster.
                </p>
                <button
                  type="submit"
                  disabled={replying || !body.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-black rounded-2xl hover:bg-gray-800 transition-all shadow-lg shadow-gray-900/20 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                >
                  <Send className="w-4 h-4" />
                  {replying ? "Sending…" : "Send Reply"}
                </button>
              </div>
            </div>
          </form>

          {/* Mark as Resolved */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black text-emerald-800">Issue resolved?</p>
                <p className="text-xs text-emerald-600 mt-0.5">Mark this ticket as resolved if your issue has been addressed.</p>
              </div>
              <button
                onClick={handleResolve}
                disabled={resolving}
                className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white text-xs font-black rounded-xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 shrink-0"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {resolving ? "Marking…" : "Mark Resolved"}
              </button>
            </div>
            {resolveError && (
              <p className="text-[10px] text-red-600 font-semibold">
                {(resolveError as any)?.data?.message
                  ?? (resolveError as any)?.error
                  ?? `HTTP ${(resolveError as any)?.status ?? (resolveError as any)?.originalStatus ?? "?"}: Could not mark as resolved.`}
              </p>
            )}
          </div>
        </div>
      ) : ticket.status === "resolved" ? (
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-black text-emerald-700">Ticket Resolved</p>
            <p className="text-xs text-emerald-600 mt-0.5">This ticket has been marked as resolved. Open a new ticket if you need further help.</p>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gray-200 flex items-center justify-center shrink-0">
            <Lock className="w-4 h-4 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-black text-gray-600">Ticket Closed</p>
            <p className="text-xs text-gray-400 mt-0.5">This ticket is closed. Open a new ticket if you need further help.</p>
          </div>
        </div>
      )}

      {/* Audit Trail */}
      {(ticket.history ?? []).length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Activity</span>
          </div>
          <div className="p-5 space-y-3">
            {(ticket.history ?? []).map((h) => (
              <div key={h.id} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-600 leading-snug">
                    <span className="font-black">{h.actor?.name ?? "System"}</span>{" "}
                    {h.action}
                    {h.from_value && h.to_value && (
                      <> from <span className="font-bold text-gray-700">{h.from_value}</span> to <span className="font-bold text-gray-700">{h.to_value}</span></>
                    )}
                    {h.note && <span className="italic text-gray-400"> — {h.note}</span>}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{fmt(h.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-900 rounded-[2.5rem] p-7 text-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-0.5">Need Faster Help?</div>
            <p className="font-semibold text-sm text-white/80 leading-snug">
              Attach screenshots or files to help our team understand your issue. We typically respond within 24 hours.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}
